import { parseEntity } from '../entity/entity';
import { entriesOf, linksRebased, parseRef, readLinks, readPlane, refKey, refText, withBlock, withLinks, withPlane, withTint } from '../plane/planeKey';
import type { PlaneLink, PlanePlacement, PlaneRef, StickyTint } from '../plane/planeKey';
import type { TagColorId } from '../design/tagPalette';

/**
 * The desk's container: the one nook file marked `container: desk` that holds where the Nooks
 * screen's desk and shelf stand, as lists of the same refs a plane reads.
 *
 *     container: desk
 *     desk:
 *       - spaces/Newest nook.md
 *       - 2026/10/Recording 2026-10-21 at 20-09.md ^4oz62hnn4jla3
 *     shelf:
 *       - spaces/The desk.md
 *     plane:
 *       - spaces/Newest nook.md @0,0 ~2.5
 *
 * `desk:` is newest first, `shelf:` in shelf order. A nook with no file yet stands as `id:<ulid>`,
 * which the app names by its path on its next desk write; this head keeps it as written, and reads
 * it as the nook whose `id` it names once that nook has a file ([NookPaths]), or else never draws
 * it. This head only ever writes a container the phone has made.
 *
 * The container is known by its id, the same on every device, as much as by its marker: an older
 * build that lists it as a nook may have renamed it away from [DESK_PATH] or dropped its marker.
 */
export const DESK_PATH = 'spaces/_desk.md';
export const DESK_ID = '01M4XRC000000000000000DESK';

const MARKER = /^container:[ \t]*desk[ \t]*$/im;
const ID_LINE = new RegExp(`^id:[ \\t]*${DESK_ID}[ \\t]*$`, 'm');
const UNFILED = 'id:';

export interface DeskEntry {
  /** The entry as written, which a move carries unchanged. */
  text: string;
  /** What it names: an `id:` entry whose nook has a file reads as that file's path. */
  ref: PlaneRef;
  /** A nook with no file yet: kept untouched, skipped when drawing. */
  unfiled: boolean;
}

/** Each loaded nook's path by its front-matter `id`, which an `id:` entry is read by. */
export type NookPaths = ReadonlyMap<string, string>;

export function nookPathsOf(nooks: readonly { id: string | null; path: string }[]): NookPaths {
  return new Map(nooks.flatMap((n) => (n.id ? [[n.id, n.path] as const] : [])));
}

export interface DeskContainer {
  desk: DeskEntry[];
  shelf: DeskEntry[];
  plane: PlanePlacement[];
  /** The connections between cards, in `plane-links:` as a nook's plane writes them. */
  links: PlaneLink[];
}

/** Whether [raw] is the container: by its id, whatever its marker says, or by its marker. */
export function isDeskContainer(raw: string): boolean {
  const front = raw.match(/^---\n([\s\S]*?)\n---/);
  return !!front && (ID_LINE.test(front[1]!) || MARKER.test(front[1]!));
}

/** The container [raw] is, or null when the file is no desk container. An `id:` entry naming one
 *  of [paths]' nooks reads as that nook's path. */
export function readDeskContainer(raw: string | null, paths?: NookPaths): DeskContainer | null {
  if (raw === null || !isDeskContainer(raw)) return null;
  return {
    desk: readEntries(raw, 'desk', paths),
    shelf: readEntries(raw, 'shelf', paths),
    plane: readPlane(parseEntity(DESK_PATH, raw)),
    links: readLinks(parseEntity(DESK_PATH, raw)),
  };
}

function readEntries(raw: string, key: 'desk' | 'shelf', paths: NookPaths | undefined): DeskEntry[] {
  return entriesOf(raw, key).flatMap((text) => {
    const ref = parseRef(text);
    if (!ref) return [];
    const entry = { text, ref, unfiled: ref.ref.startsWith(UNFILED) };
    return [paths ? filedEntry(entry, paths) : entry];
  });
}

/** [entry] as [paths] read it: an `id:` entry whose nook has a file names that file, its text as written. */
export function filedEntry(entry: DeskEntry, paths: NookPaths): DeskEntry {
  if (!entry.unfiled) return entry;
  const path = paths.get(entry.ref.ref.slice(UNFILED.length));
  return path ? { text: entry.text, ref: { ref: path, anchors: [] }, unfiled: false } : entry;
}

/** An entry for a ref that was not listed before. */
export function entryFor(ref: PlaneRef): DeskEntry {
  return { text: refText(ref), ref, unfiled: false };
}

type ListKey = 'desk' | 'shelf';

function withEntries(raw: string, key: ListKey, entries: readonly DeskEntry[]): string {
  return withBlock(raw, key, entries.map((e) => e.text));
}

/** [entries] without any that stand for [key]'s card. */
function without(entries: readonly DeskEntry[], key: string): DeskEntry[] {
  return entries.filter((e) => e.unfiled || refKey(e.ref) !== key);
}

const has = (entries: readonly DeskEntry[], key: string) => entries.some((e) => !e.unfiled && refKey(e.ref) === key);

/**
 * [raw] with the cards the desk shows but no list holds (pinned or draft nooks with no entry)
 * listed at the front of `desk:`, in the order they show. [shown] is the desk's cards in order;
 * one with no file path is never listed, nor is one the shelf lists, which a device may have put
 * there since the desk was read.
 */
export function withShownListed(raw: string, shown: readonly { key: string; entry: DeskEntry }[], paths?: NookPaths): string {
  const read = readDeskContainer(raw, paths);
  if (!read) return raw;
  const missing = shown.filter(
    (s) => !s.entry.unfiled && !s.entry.ref.ref.startsWith(UNFILED) && !has(read.desk, s.key) && !has(read.shelf, s.key),
  );
  if (missing.length === 0) return raw;
  return withEntries(raw, 'desk', [...missing.map((s) => s.entry), ...read.desk]);
}

/** [raw] with [entry] listed on the desk, at its front; already listed, it stays where it is. */
export function withOnDesk(raw: string, entry: DeskEntry, paths?: NookPaths): string {
  const read = readDeskContainer(raw, paths);
  if (!read || has(read.desk, refKey(entry.ref))) return raw;
  return withEntries(raw, 'desk', [entry, ...read.desk]);
}

/** [raw] with the card [key] off `desk:`; its plane line and the shelf are left as they are. */
export function withOffDesk(raw: string, key: string, paths?: NookPaths): string {
  const read = readDeskContainer(raw, paths);
  if (!read || !has(read.desk, key)) return raw;
  return withEntries(raw, 'desk', without(read.desk, key));
}

/** A card taken from the shelf: off `shelf:`, at the front of `desk:`. */
export function withFromShelf(raw: string, entry: DeskEntry, paths?: NookPaths): string {
  const read = readDeskContainer(raw, paths);
  if (!read) return raw;
  const key = refKey(entry.ref);
  const found = read.shelf.find((e) => !e.unfiled && refKey(e.ref) === key);
  const onShelf = withEntries(raw, 'shelf', without(read.shelf, key));
  return withOnDeskFront(onShelf, found ?? entry, paths);
}

function withOnDeskFront(raw: string, entry: DeskEntry, paths: NookPaths | undefined): string {
  const read = readDeskContainer(raw, paths)!;
  return withEntries(raw, 'desk', [entry, ...without(read.desk, refKey(entry.ref))]);
}

/** A card put on the shelf: off `desk:`, at the front of `shelf:`; its plane line stays. */
export function withToShelf(raw: string, entry: DeskEntry, paths?: NookPaths): string {
  const read = readDeskContainer(raw, paths);
  if (!read) return raw;
  const key = refKey(entry.ref);
  const found = read.desk.find((e) => !e.unfiled && refKey(e.ref) === key);
  const offDesk = withEntries(raw, 'desk', without(read.desk, key));
  return withEntries(offDesk, 'shelf', [found ?? entry, ...without(read.shelf, key)]);
}

/**
 * The shelf in a new order. [order] is the keys of the cards the panel shows; an entry the panel
 * does not show (a nook with no file yet) keeps its slot.
 */
export function withShelfOrder(raw: string, order: readonly string[], paths?: NookPaths): string {
  const read = readDeskContainer(raw, paths);
  if (!read) return raw;
  const byKey = new Map(read.shelf.filter((e) => !e.unfiled).map((e) => [refKey(e.ref), e]));
  const queue = order.flatMap((key) => {
    const entry = byKey.get(key);
    return entry ? [entry] : [];
  });
  const shown = new Set(queue.map((e) => refKey(e.ref)));
  const next = read.shelf.map((e) => {
    if (e.unfiled || !shown.has(refKey(e.ref))) return e;
    return queue.shift() ?? e;
  });
  return withEntries(raw, 'shelf', next);
}

/** [raw] with the card at [at] placed there, its other words kept; one not placed yet is added. */
export function withCardAt(raw: string, ref: PlaneRef, at: { x: number; y: number }): string {
  if (!readDeskContainer(raw)) return raw;
  const placements = readPlane(parseEntity(DESK_PATH, raw));
  const key = refKey(ref);
  const found = placements.some((p) => refKey(p) === key);
  const next = found
    ? placements.map((p) => (refKey(p) === key ? { ...p, x: at.x, y: at.y } : p))
    : [...placements, { ref: ref.ref, anchors: ref.anchors, x: at.x, y: at.y }];
  return withPlane(raw, next);
}

/** [raw] with the card placed at [at] when it has no plane line, and as it was when it has: where it
 *  stands is the person's once they have acted on it, and only a drag moves it. */
export function withCardPlaced(raw: string, ref: PlaneRef, at: { x: number; y: number }): string {
  const key = refKey(ref);
  return readPlane(parseEntity(DESK_PATH, raw)).some((p) => refKey(p) === key) ? raw : withCardAt(raw, ref, at);
}

/** [raw] with the card drawn in [tint], placed at [at] first where it has no plane line. */
export function withCardTint(raw: string, ref: PlaneRef, at: { x: number; y: number }, tint: StickyTint | TagColorId): string {
  const key = refKey(ref);
  const placed = withCardPlaced(raw, ref, at);
  return withPlane(placed, readPlane(parseEntity(DESK_PATH, placed)).map((p) => (refKey(p) === key ? withTint(p, tint) : p)));
}

/** [raw] with a sticky drawn at [size], placed at [at] first where it has no plane line. */
export function withCardSize(raw: string, ref: PlaneRef, at: { x: number; y: number }, size: { w: number; h: number }): string {
  const key = refKey(ref);
  const placed = withCardPlaced(raw, ref, at);
  return withPlane(placed, readPlane(parseEntity(DESK_PATH, placed)).map((p) => (refKey(p) === key ? { ...p, w: size.w, h: size.h } : p)));
}

/**
 * [raw] with the connections [after], the page's edit of [before], made again on the connections as
 * the file holds them now. Each of [ends] is placed first, at the spot it is drawn at, where it has no
 * plane line: a connection joins cards, and a card the connection is drawn to has stood where it is
 * drawn all along.
 */
export function withDeskLinks(
  raw: string,
  before: readonly PlaneLink[],
  after: readonly PlaneLink[],
  ends: readonly { ref: PlaneRef; at: { x: number; y: number } }[],
): string {
  if (!readDeskContainer(raw)) return raw;
  const placed = ends.reduce((text, end) => withCardPlaced(text, end.ref, end.at), raw);
  return withLinks(placed, linksRebased(readLinks(parseEntity(DESK_PATH, placed)), before, after));
}
